/**
 * Hash routing.
 *
 * ## Why the hash and not the History API
 *
 * Slate's client is a static bundle served by Cloudflare Pages. A path-based route
 * needs a rewrite rule that sends every unknown path back to `index.html`, and a
 * rewrite rule is a deployment configuration that has to be right in every
 * environment or a deep link 404s in one of them. The hash is handled entirely by
 * the browser: `#/w/acme/b/<id>` reaches the server as `/`, which is a file that
 * exists.
 *
 * The cost is an uglier URL and no server-side rendering of a board route. Neither
 * matters here: there is no SEO story for a private board, and the board is a live
 * document with no meaningful static rendering.
 *
 * ## Why a hand-written router and not `react-router`
 *
 * There are two routes. `react-router` is 20 KB and brings a matcher, a history
 * abstraction, nested layouts and a data-loading API, none of which this
 * application uses. What it would not bring is any of the logic below, which is
 * the part that is actually specific to Slate: the route encodes a workspace slug
 * *and* a board id, and changing either one must tear down and rebuild the board's
 * socket and its snapshot.
 */

import { useEffect, useState } from 'react';

export interface Route {
  workspaceSlug: string | null;
  boardId: string | null;
}

const EMPTY: Route = { workspaceSlug: null, boardId: null };

/**
 * Parse `#/w/<slug>/b/<boardId>`.
 *
 * Tolerant rather than strict. A URL is user-editable and a malformed one should
 * land on the workspace list, not on an error page - there is nothing a user could
 * do with "invalid route" that they cannot do with the list in front of them.
 *
 * `decodeURIComponent` is applied per segment and wrapped, because a `%` that is
 * not part of a valid escape sequence throws, and a thrown error inside a
 * `hashchange` listener leaves the application on a blank screen.
 */
export function parseHash(hash: string): Route {
  const path = hash.startsWith('#') ? hash.slice(1) : hash;
  const segments = path.split('/').filter((segment) => segment !== '');

  const decode = (value: string): string => {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  };

  let workspaceSlug: string | null = null;
  let boardId: string | null = null;

  for (let index = 0; index < segments.length; index += 1) {
    if (segments[index] === 'w') workspaceSlug = decode(segments[index + 1] ?? '') || null;
    if (segments[index] === 'b') boardId = decode(segments[index + 1] ?? '') || null;
  }

  return { workspaceSlug, boardId };
}

export function buildHash(route: Route): string {
  const parts: string[] = [];
  if (route.workspaceSlug !== null) parts.push('w', encodeURIComponent(route.workspaceSlug));
  if (route.boardId !== null) parts.push('b', encodeURIComponent(route.boardId));
  return parts.length === 0 ? '#/' : `#/${parts.join('/')}`;
}

export function navigate(route: Route): void {
  const next = buildHash(route);
  if (window.location.hash === next) return;
  window.location.hash = next;
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseHash(window.location.hash));

  useEffect(() => {
    const onChange = () => setRoute(parseHash(window.location.hash));
    window.addEventListener('hashchange', onChange);
    // The initial hash is read in the state initialiser above, so this listener
    // only ever sees subsequent changes - but a `hashchange` that fired between
    // the initial render and this effect would be missed, which is why the
    // handler is also called once here.
    onChange();
    return () => window.removeEventListener('hashchange', onChange);
  }, []);

  return route;
}

export { EMPTY as EMPTY_ROUTE };
